import { Button } from 'common/components/Button/Button'
import { pagesPaths } from 'common/router/routes.utils'
import { freeDomains } from 'common/utils/freeDomains'
import { validCompanyName, validName } from 'common/utils/validation/regexes'
import { useFormik } from 'formik'
import { useAtom } from 'jotai'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { recruiterRoleEnum } from 'services/companies/companies.enums'
import styled from 'styled-components'
import useSWRMutation from 'swr/mutation'
import * as Yup from 'yup'
import YupPassword from 'yup-password'
import { Input } from '../../../common/components/Forms/Inputs/Input'
import { errorMessagesSignUp as errorMessages } from '../../../common/utils/validation/messages'
import { userAccountStatusEnum, userCategoryEnum } from '../auth.enums'
import { ApiPathsUsers, login, register } from '../auth.service'
import { auth } from '../auth.singleton'
import { showCompanySignUpDialogAtom } from '../auth.state'
import { IRegisterUser } from '../auth.types'
import { initialRegisterState } from './auth.models'
YupPassword(Yup)

export const SignUpComponent = () => {
  //utils
  const router = useRouter()

  //state
  const [, setShowCompanySignUpModal] = useAtom(showCompanySignUpDialogAtom)
  const {
    email,
    companyName,
    role = recruiterRoleEnum.admin,
  } = router.query as { email: string; companyName: string; role?: recruiterRoleEnum }

  //form
  const SignUpSchema = Yup.object().shape({
    firstName: Yup.string()
      .required(`Firstname ${errorMessages.isRequired}`)
      .matches(validName, `Firstname ${errorMessages.validName}`)
      .max(50, `Firstname ${errorMessages.maxLength}`),
    lastName: Yup.string()
      .required(`Lastname ${errorMessages.isRequired}`)
      .matches(validName, `Lastname ${errorMessages.validName}`)
      .max(50, `Lastname ${errorMessages.maxLength}`),
    companyName: Yup.string()
      .required(`Company name ${errorMessages.isRequired}`)
      .matches(validCompanyName, `Company name ${errorMessages.validName}`)
      .max(50, `Company name ${errorMessages.maxLength}`),
    email: Yup.string()
      .required(`Email ${errorMessages.isRequired}`)
      .email(errorMessages.isEmail)
      .test('isFreeDomain', errorMessages.freeDomainEmail, (value) => {
        if (!value) return true // Skip validation if the value is empty
        const domain = value.split('@')[1]
        return !freeDomains.includes(domain)
      })
      .max(50, `Email ${errorMessages.maxLength}`),
    password: Yup.string()
      .required(`Password ${errorMessages.isRequired}`)
      .min(8, `Password ${errorMessages.minLength}`)
      .max(30, `Password ${errorMessages.maxLengthPassword}`)
      .minUppercase(1, errorMessages.oneUppercase)
      .minLowercase(1, errorMessages.oneLowercase)
      .minNumbers(1, errorMessages.oneDigit)
      .minSymbols(1, errorMessages.oneSpecialCharacter),
  })

  // api
  const { trigger: triggerRegister, isMutating } = useSWRMutation(ApiPathsUsers.register, register, {
    onError(error) {
      const message = error?.response?.data?.error || error?.response?.data?.message || 'Something went wrong...'
      toast.error(message)
    },
    async onSuccess(user) {
      if (role === recruiterRoleEnum.regular) {
        toast.success('Successfully registered!')
        return router.push(pagesPaths.login)
      }
      await triggerLogin({ email: user.email, password: user.password })
    },
  })

  const { trigger: triggerLogin, isMutating: isLoadingLogin } = useSWRMutation(ApiPathsUsers.login, login, {
    onError(error) {
      if (!error) {
        toast.error('Could not log you in! Please try to login manually')
        router.push(pagesPaths.login)
      }
    },
    onSuccess(user) {
      auth.setSession(user)

      if (user.status === userAccountStatusEnum.pendingVerification) {
        return router.push(pagesPaths.verifyAccount)
      }

      if ([userCategoryEnum.VALIDATOR, userCategoryEnum.ADMIN].includes(user.category)) {
        return router.push(pagesPaths.activeAccounts)
      }

      if (user.status === userAccountStatusEnum.active) {
        return router.replace(pagesPaths.dashboard)
      }
    },
  })

  const onSubmit = async (values: IRegisterUser) => {
    await triggerRegister({ ...values, role, email: values.email.toLowerCase() })
  }

  const { setFieldValue, values, errors, touched, setFieldTouched, handleSubmit } = useFormik({
    initialValues: initialRegisterState,
    onSubmit,
    validationSchema: SignUpSchema,
    enableReinitialize: true,
  })

  const handleSingupSubmit = (e: any) => {
    const errorsKeys = Object.keys(errors)
    if (errorsKeys.length > 1) {
      toast.error('Invalid data provided. Please check the information and try again.')
    }

    handleSubmit(e)
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldTouched(e.target.name, false)
    setFieldValue(e.target.name, e.target.value)
  }

  useEffect(() => {
    if (email && companyName) {
      setFieldValue('email', email.replaceAll(' ', '+'))
      setFieldValue('companyName', companyName)
      setFieldValue('role', 'regular')
      setFieldValue('category', 2)
    }
  }, [companyName, email, setFieldValue])

  const isLoading = isMutating || isLoadingLogin

  return (
    <>
      <TextContainer>
        <p>Hello There 🖐</p>
        <h3>Welcome to Seaplify.</h3>
      </TextContainer>

      <form onSubmit={handleSingupSubmit} className="px-4 sm:px-0">
        <div className="flex flex-column gap-3">
          <div className="flex gap-2">
            <Input
              name="firstName"
              value={values?.firstName}
              label="First Name"
              placeholder="Type here"
              onChange={onChange}
              error={touched?.firstName && errors?.firstName}
              data-cy="sign-up-input-first-name"
            />
            <Input
              name="lastName"
              value={values?.lastName}
              label="Last Name"
              placeholder="Type here"
              onChange={onChange}
              error={touched?.lastName && errors?.lastName}
              data-cy="sign-up-input-last-name"
            />
          </div>
          <Input
            name="companyName"
            value={values?.companyName}
            onChange={onChange}
            disabled={role === recruiterRoleEnum.regular}
            label="Company name"
            placeholder="Type here"
            error={touched?.companyName && errors?.companyName}
            data-cy="sign-up-input-company-name"
          />
          <Input
            name="email"
            value={values?.email}
            label="Work email"
            placeholder="Type here"
            disabled={role === recruiterRoleEnum.regular}
            error={touched?.email && errors?.email}
            onChange={onChange}
            data-cy="sign-up-input-email"
          />
          <Input
            name="password"
            label="Password"
            type="password"
            placeholder="Type here"
            onChange={onChange}
            error={touched?.password && errors?.password}
            data-cy="sign-up-input-password"
          />
        </div>

        <Button
          type="submit"
          className="w-full my-5 border-round-lg"
          size="large"
          isLoading={isLoading}
          data-cy="sign-up-button-submit"
        >
          Continue
        </Button>
      </form>

      <TextContainer>
        <p className="footerText">
          Already have an account? Please{' '}
          <Link className="secondary_100" href="/login" data-cy="sign-up-link-sign-in">
            Sign In
          </Link>
        </p>
      </TextContainer>
      <TextContainer>
        <p className="terms-text">
          By tapping Sign up, you agree to our{' '}
          <Link className="font-bold" href="/terms-and-conditions" data-cy="sign-up-link-terms-and-conditions">
            {' '}
            Terms of Use
          </Link>
          ,
          <Link className="font-bold" href="/privacy-policy" data-cy="sign-up-link-privacy-policy">
            {' '}
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link className="font-bold" href="/terms-and-conditions" data-cy="sign-up-link-terms-and-conditions">
            {' '}
            Cookies Policy
          </Link>
          , and to receive offers and updates.
        </p>
      </TextContainer>
    </>
  )
}

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  h3 {
    font-weight: 600;
    font-size: 22px;
    line-height: 140%;
  }
  p {
    font-weight: 400;
    font-size: 14px;
    line-height: 180%;
  }
  .footerText {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 180%;
  }
  .terms-text {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    color: #4f4f4f;
  }
  margin-bottom: 2rem;
`
